<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in sidebarRoutes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="routeLink(route)"
            :href="route.meta?.external ? route.meta.href : undefined"
            :target="route.meta?.external ? '_blank' : undefined"
            :rel="route.meta?.external ? 'noopener noreferrer' : undefined"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pe-2 ps-4">
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ t(route.displayName) }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              :to="{
                name: childRoute.name,
                ...(childRoute.meta?.query && {
                  query: { query: childRoute.meta.query },
                }),
              }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(childRoute.displayName)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pe-2 ps-11">
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ t(childRoute.displayName) }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t, locale } = useI18n()

    const value = ref<boolean[]>([])

    const sidebarRoutes = computed(() => navigationRoutes.routes)

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeLink = (item: INavigationRoute) => {
      if (item.children || item.meta?.external) return undefined
      return {
        name: item.name,
        ...(item.meta?.query && {
          query: { query: item.meta.query },
        }),
      }
    }

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (section.meta?.external) return false

      const isSameRoute = (item: INavigationRoute) => {
        if (route.name !== item.name) return false

        if (item.meta?.query) {
          return route.query.query === item.meta.query
        }

        return true
      }

      if (!section.children) {
        return isSameRoute(section)
      }

      return section.children.some(isSameRoute)
    }

    const setActiveExpand = () =>
      (value.value = sidebarRoutes.value.map((routeItem: INavigationRoute) => routeHasActiveChild(routeItem)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (routeItem: INavigationRoute) => (routeHasActiveChild(routeItem) ? 'primary' : 'secondary')
    const textColor = (routeItem: INavigationRoute) => (routeHasActiveChild(routeItem) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })
    watch(locale, setActiveExpand)

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      sidebarRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      routeLink,
      t,
      iconColor,
      textColor,
      arrowDirection,
    }
  },
})
</script>
